import React, { useState, useEffect } from 'react';
import { Menu, X, Heart, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#storia', label: t('nav.storia') },
    { href: '#dettagli', label: t('nav.dettagli') },
    { href: '#countdown', label: t('nav.countdown') },
    { href: '#gallery', label: t('nav.gallery') },
    { href: '#regalo', label: t('nav.dono') },
    { href: '#rsvp', label: t('nav.rsvp') }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const langToggle = (
    <button
      onClick={() => setLang(lang === 'it' ? 'es' : 'it')}
      className="flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-lavender hover:bg-lavender hover:text-white transition-colors duration-300"
      aria-label="Cambia lingua / Cambiar idioma"
      data-testid="language-toggle"
    >
      <Globe className="w-4 h-4" />
      <span className="font-display text-xs uppercase tracking-[0.2em]">{lang === 'it' ? 'ES' : 'IT'}</span>
    </button>
  );

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur-md shadow-soft py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo / Names */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center gap-3 group"
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full border-2 border-gold/40 flex items-center justify-center bg-lavender-light/20 group-hover:border-gold/60 transition-colors">
                  <Heart className="w-4 h-4 text-gold" fill="currentColor" />
                </div>
              </div>
              <span className={`font-script text-2xl text-lavender transition-colors group-hover:text-lavender-dark`}>
                J & M
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="font-display text-sm uppercase tracking-[0.2em] transition-colors relative group text-foreground"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300" />
                </a>
              ))}
              {langToggle}
            </div>

            {/* Mobile: language toggle + menu button */}
            <div className="flex md:hidden items-center gap-3">
              {langToggle}
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-lavender/10"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5 text-foreground" />
                ) : (
                  <Menu className="w-5 h-5 text-foreground" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-background/98 backdrop-blur-lg transition-all duration-500 md:hidden ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Decorative background */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-lavender/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-blush/20 rounded-full blur-3xl" />
        <div className="absolute inset-0 damask-pattern opacity-30" />

        <div className="flex flex-col items-center justify-center h-full gap-10 relative">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-20 h-20 rounded-full border-2 border-gold/40 flex items-center justify-center bg-lavender-light/20">
              <Heart className="w-8 h-8 text-gold" fill="currentColor" />
            </div>
          </div>

          {navLinks.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className={`font-display text-2xl text-foreground hover:text-lavender transition-all duration-500 tracking-wider ${
                isMobileMenuOpen
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${100 + index * 50}ms` }}
            >
              {link.label}
            </a>
          ))}

          {/* Decorative element */}
          <div className="flex items-center gap-4 mt-8">
            <span className="w-12 h-px bg-gold/40" />
            <span className="text-lavender/50">❦</span>
            <span className="w-12 h-px bg-gold/40" />
          </div>

          {/* Date */}
          <p className="font-elegant text-xl text-muted-foreground italic mt-4">
            {t('date.full')}
          </p>
        </div>
      </div>
    </>
  );
};

export default Navigation;
